import { FloatingFormItem, Group, InputDecimal } from '@/components/_common';
import { useEmployeeDetail } from '@/core/domain/enterprise/Employees/Employees.hooks';
import {
  CreateWorkOrderCommissionRequest,
  WorkOrderCommission,
} from '@/core/domain/workorder/WorkOrdersCommisions/WorkOrdersCommissions.types';
import { WorkOrderItemType } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import { useDefaultEmptyFieldError, useDisclosure } from '@/hooks';
import { Alert, Button, Col, Form, FormInstance, Row, Spin } from 'antd';
import { useCallback, useEffect, useMemo } from 'react';
import { useWorkorderDetailContext } from '../../../WorkorderDetailProvider';
import EmployeesByCompanySelectField from '@/components/_common/EmployeesByCompanySelectField';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { usePartStockPrices } from '@/core/domain/stock/StockItems/StockItems.hooks';
import { useTask } from '@/core/domain/operational/Tasks/Tasks.hooks';

type Props = {
  form: FormInstance<CreateWorkOrderCommissionRequest>;
  itemType?: WorkOrderItemType;
  baseValue?: number;
  maxQuantity: number;
  employeesSelected: WorkOrderCommission[];
  employeeId?: number;
  itemId?: number;
};

const CommisionForm: React.FC<Props> = ({
  form,
  itemType,
  baseValue,
  maxQuantity,
  employeesSelected,
  employeeId,
  itemId,
}) => {
  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const [alertOpened, alertActions] = useDisclosure();

  const { data: employee, mutateAsync: getEmployee } = useEmployeeDetail();

  const professional = employee?.professionalInformation;

  const increaseCommissionAboveRegisteredLimit = hasPermissions([
    Authorities.INCREASE_COMMISSION_ABOVE_REGISTERED_LIMIT,
  ]);

  const {
    workorder: { data: workorder },
  } = useWorkorderDetailContext();

  const partStockPrices = usePartStockPrices(
    itemId,
    itemType === WorkOrderItemType.Part,
  );

  const taskDetail = useTask(itemId, itemType === WorkOrderItemType.Service);

  const getPartCommission = useCallback(() => {
    const stockPriceList = partStockPrices.data?.result || [];
    const stockPrices = stockPriceList.find(
      (stock) => stock.company.id === workorder?.company.id,
    );
    return (stockPrices?.commissionPercentage || 0) * 100;
  }, [partStockPrices.data, workorder]);

  const getTaskCommission = useCallback(() => {
    const taskPriceList = taskDetail.data?.taskPriceSet || [];
    const taskPrices = taskPriceList.find(
      (stock) => stock.companyId === workorder?.company.id,
    );
    return taskPrices?.commissionPercentage || 0;
  }, [taskDetail.data, workorder]);

  const percent = useMemo(() => {
    if (itemType === WorkOrderItemType.Part) {
      const partCommission = getPartCommission();
      return partCommission || professional?.partCommission || undefined;
    }

    if (itemType === WorkOrderItemType.Service) {
      const taskCommission = getTaskCommission();
      return taskCommission || professional?.serviceCommission || undefined;
    }

    return undefined;
  }, [getPartCommission, getTaskCommission, professional]);

  useEffect(() => {
    form.setFieldsValue({
      percent: percent ?? 0,
      quantity: maxQuantity,
    });
  }, [percent]);

  useEffect(() => {
    if (!!employeeId) {
      alertActions.open();
    }
  }, [employeeId]);

  const selectEmployee = async () => {
    if (employeeId) {
      await getEmployee(employeeId);
      form.setFieldValue('employeeId', employeeId);
    }

    alertActions.close();
  };

  return (
    <Form
      form={form}
      preserve={false}
      onValuesChange={async (fields) => {
        if (fields.employeeId) {
          await getEmployee(fields.employeeId);
        }
      }}
    >
      <Row gutter={[16, 8]}>
        {alertOpened && (
          <Col span={24}>
            <Alert
              style={{ marginBottom: 24 }}
              type="warning"
              description={
                <>
                  <div style={{ marginBottom: 16 }}>
                    Existe um funcionário relacionado ao serviço, deseja
                    vincula-lo na comissão deste serviço?
                  </div>

                  <Group gap={24}>
                    <Button type="primary" onClick={selectEmployee}>
                      Selecionar funcionário
                    </Button>
                  </Group>
                </>
              }
            />
          </Col>
        )}

        <Col span={24}>
          <FloatingFormItem
            name="employeeId"
            label="Funcionário"
            rules={[defaultEmptyFieldError]}
          >
            <EmployeesByCompanySelectField
              companyId={workorder?.company.id}
              employeesDisabled={employeesSelected.map(
                (item) => item.employee.id,
              )}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24}>
          <FloatingFormItem
            name="quantity"
            label="Quantidade"
            rules={[
              defaultEmptyFieldError,
              { min: 0, type: 'number', message: 'Quantidade mínima de 0' },
              {
                max: maxQuantity,
                type: 'number',
                message: `Quantidade máxima de ${maxQuantity}`,
              },
            ]}
          >
            <InputDecimal min={0} max={maxQuantity} />
          </FloatingFormItem>
        </Col>

        <Col span={24}>
          <FloatingFormItem
            name="percent"
            label="Comissão (%)"
            rules={[
              defaultEmptyFieldError,
              { min: 0, type: 'number', message: 'Comissão mínima de 0%' },
              {
                max: increaseCommissionAboveRegisteredLimit
                  ? undefined
                  : percent,
                type: 'number',
                message: `Comissão máxima de ${percent}%`,
              },
            ]}
          >
            <InputDecimal
              min={0}
              max={increaseCommissionAboveRegisteredLimit ? undefined : percent}
              disabled={partStockPrices.isLoading}
              suffix={partStockPrices.isLoading ? <Spin size="small" /> : null}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24}>
          <FloatingFormItem
            name="baseValue"
            label="Valor base ($)"
            rules={[defaultEmptyFieldError]}
            initialValue={baseValue}
          >
            <InputDecimal
              disabled={
                !hasPermissions([
                  Authorities.MOVIMENTATION_WORKORDER_CHANGE_BASE_COMMISSION_VALUE,
                ])
              }
              min={0}
              max={
                !hasPermissions([
                  Authorities.MOVIMENTATION_WORKORDER_ITEM_ADD_BASE_COMMISSION_VALUE_GREATER_THAN_NET_VALUE,
                ])
                  ? baseValue
                  : undefined
              }
            />
          </FloatingFormItem>
        </Col>
      </Row>
    </Form>
  );
};

export default CommisionForm;
